(ns arthur.domain.zip
  "A ZIP with STORED entries, for shipping a frame sequence and its audio as one
  file.

  STORED — compression method 0, the bytes verbatim — because every entry going
  into it is already deflated (a PNG's IDAT) or is PCM that the user is about to
  hand a codec (the WAV). Deflating a deflated stream buys nothing and costs a
  pass over every byte of a long export, and it is also what lets this namespace
  be about the container alone: an archive with no compressor in it is a handful
  of little-endian headers, and there is no dependency to vendor.

  WHY A ZIP AND NOT A DIRECTORY. `showDirectoryPicker` would write the numbered
  sequence straight to disk, which is closer to what an NLE wants, and it does not
  exist in Firefox — which is a browser this tool is already known to behave
  differently in (see `flow/ingest` on seeking). One archive downloads the same
  way everywhere and pairs the picture with the sound it has to stay in sync with,
  so the two cannot be separated on the way to the cutting room.

  NO ZIP64. The offsets and sizes here are 32-bit, so this refuses an archive at
  4GB rather than writing one whose central directory silently wraps. A 900-frame
  export at 1920x1200 is tens of megabytes, so the limit is not in the way; a
  limit that corrupts instead of refusing would be."
  (:require [arthur.domain.crc32 :as crc32]))

(def ^:private limit
  "The largest archive this writer will produce. Past it the format needs Zip64
  and every offset below would have to be 64-bit."
  0xffffffff)

(defn- bytes-of [^String s]
  ;; ASCII by construction — `0001.png`, `audio.wav` — and asserted rather than
  ;; assumed, because a non-ASCII name would need the UTF-8 general-purpose flag
  ;; and would otherwise arrive mojibake'd in the archive.
  (let [out (js/Uint8Array. (.-length s))]
    (dotimes [i (.-length s)]
      (let [c (.charCodeAt s i)]
        (when (> c 127)
          (throw (ex-info "a zip entry name must be ASCII" {:name s})))
        (aset out i c)))
    out))

(defn- u16! [^js b at n]
  (aset b at (bit-and n 0xff))
  (aset b (+ at 1) (bit-and (unsigned-bit-shift-right n 8) 0xff)))

(defn- u32! [^js b at n]
  (u16! b at (bit-and n 0xffff))
  (u16! b (+ at 2) (unsigned-bit-shift-right n 16)))

(defn dos-time
  "A `js/Date` as the two 16-bit fields ZIP inherited from MS-DOS: [date time].

  Seconds have one bit less than they need, so they land on even values, and the
  year is an offset from 1980. Both are the format's, not an approximation — a
  date before 1980 is not representable and is clamped rather than wrapped into a
  plausible-looking wrong one."
  [^js d]
  [(bit-or (bit-shift-left (max 0 (- (.getFullYear d) 1980)) 9)
           (bit-shift-left (inc (.getMonth d)) 5)
           (.getDate d))
   (bit-or (bit-shift-left (.getHours d) 11)
           (bit-shift-left (.getMinutes d) 5)
           (quot (.getSeconds d) 2))])

(defn archive
  "Entries -> the parts of one ZIP, ready for a `js/Blob`.

  Each entry is `{:name \"0001.png\" :data <Uint8Array>}`. Returns a vector of
  byte arrays rather than one buffer: the payloads are already in memory and a
  long export is tens of megabytes, so the archive REFERS to them instead of
  copying every one into a second buffer of the same size. `js/Blob` takes the
  parts as they are.

  `at` is the modification time stamped on every entry. Passed in rather than read
  from the clock so that the same frames produce the same archive, byte for byte,
  which is what makes it assertable."
  ([entries] (archive entries (js/Date.)))
  ([entries ^js at]
   (let [[date time] (dos-time at)
         ;; One pass, because a central directory entry needs the local header's
         ;; OFFSET and therefore the running total, and the CRC is wanted in both
         ;; places. Building the two lists separately would mean either computing
         ;; every CRC twice or keeping a parallel vector of them.
         {:keys [parts central offset]}
         (reduce
          (fn [{:keys [parts central offset]} {:keys [name data]}]
            (let [nm    (bytes-of name)
                  n     (.-length nm)
                  size  (.-length ^js data)
                  crc   (crc32/of data)
                  local (js/Uint8Array. (+ 30 n))
                  dir   (js/Uint8Array. (+ 46 n))]
              (u32! local 0 0x04034b50)     ; local file header
              (u16! local 4 10)             ; version needed: 1.0 is enough to store
              (u16! local 6 0)              ; no flags; the name is ASCII
              (u16! local 8 0)              ; method 0: stored
              (u16! local 10 time)
              (u16! local 12 date)
              (u32! local 14 crc)
              (u32! local 18 size)          ; compressed size — the same, stored
              (u32! local 22 size)
              (u16! local 26 n)
              (u16! local 28 0)             ; no extra field
              (.set local nm 30)

              (u32! dir 0 0x02014b50)       ; central directory header
              (u16! dir 4 10)               ; made by
              (u16! dir 6 10)               ; version needed
              (u16! dir 8 0)
              (u16! dir 10 0)
              (u16! dir 12 time)
              (u16! dir 14 date)
              (u32! dir 16 crc)
              (u32! dir 20 size)
              (u32! dir 24 size)
              (u16! dir 28 n)
              (u16! dir 30 0)               ; extra
              (u16! dir 32 0)               ; comment
              (u16! dir 34 0)               ; disk number
              (u16! dir 36 0)               ; internal attributes
              (u32! dir 38 0)               ; external attributes
              (u32! dir 42 offset)
              (.set dir nm 46)

              (when (> (+ offset (.-length local) size) limit)
                (throw (ex-info "this export is too big for a zip without Zip64"
                                {:bytes (+ offset (.-length local) size)})))
              {:parts   (conj parts local data)
               :central (conj central dir)
               :offset  (+ offset (.-length local) size)}))
          {:parts [] :central [] :offset 0}
          entries)
         dir-size (transduce (map #(.-length ^js %)) + 0 central)
         end      (js/Uint8Array. 22)]
     (u32! end 0 0x06054b50)                ; end of central directory
     (u16! end 4 0)                         ; this disk
     (u16! end 6 0)                         ; the disk the directory starts on
     (u16! end 8 (count central))
     (u16! end 10 (count central))
     (u32! end 12 dir-size)
     (u32! end 16 offset)
     (u16! end 20 0)                        ; no archive comment
     (-> (into parts central) (conj end) vec))))

(defn blob
  "The archive as one `js/Blob`, which is what a download wants."
  ([entries] (blob entries (js/Date.)))
  ([entries at]
   (js/Blob. (into-array (archive entries at)) #js {:type "application/zip"})))
